import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createConsumer } from '@rails/actioncable'

import { useHttp } from './Http'

const ENDPOINT = '/api/v1/notifications'

type NotificationCallback = (data: Record<string, unknown>) => void

interface NotificationsServiceState {
  consumer: ReturnType<typeof createConsumer> | undefined
  subscription: { unsubscribe: () => void } | undefined
  subscribers: Record<string, NotificationCallback | undefined>
}

export function createNotificationsService(scope: string) {
  const state: NotificationsServiceState = {
    consumer: undefined,
    subscription: undefined,
    subscribers: {},
  }

  return function useNotificationsService() {
    const http = useHttp()
    const [nativeNotificationsEnabled, setNativeNotificationsEnabled] = useState<boolean | undefined>(undefined)

    const { data: notifications = [] } = useQuery({
      queryKey: [scope, 'notifications'],
      queryFn: () => http.apiGet(ENDPOINT) as Promise<Record<string, unknown>[]>,
    })

    const initialize = () => {
      if (('Notification' in window) && window.Notification.permission === 'granted') {
        setNativeNotificationsEnabled(true)
      } else if (('Notification' in window) && window.Notification.permission !== 'denied') {
        window.Notification.requestPermission().then((permission) => {
          setNativeNotificationsEnabled(permission === 'granted')
        })
      }

      // Authenticate the cable via the session cookie (sent on the same-origin
      // handshake), not a token in the URL. Gate on being logged in.
      const isAuthenticated = localStorage.getItem('userData') != null
      if (isAuthenticated && !state.consumer) {
        state.consumer = createConsumer('/cable')
        state.subscription = state.consumer.subscriptions.create('NotificationsChannel', {
          received(data: Record<string, unknown>) {
            Object.keys(state.subscribers)
              .filter((k) => state.subscribers[k])
              .forEach((k) => state.subscribers[k]!(data))
          }
        })
      } else if (!isAuthenticated) {
        state.subscription?.unsubscribe()
        state.subscription = undefined

        state.consumer?.disconnect()
        state.consumer = undefined
      }
    }

    const subscribe = (key: string, callback: NotificationCallback) => {
      state.subscribers[key] = callback
    }

    const unsubscribe = (key: string) => {
      state.subscribers[key] = undefined
    }

    return {
      initialize,
      nativeNotificationsEnabled,
      subscribe,
      unsubscribe,
      notifications,
    }
  }
}
